import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { useTenantStore } from '../stores/tenant';
import { useSubscription } from '../composables/useSubscription';
import { trackPageView } from '../lib/analytics';
import Dashboard from '../views/Dashboard.vue';

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.hash) return { el: to.hash, behavior: 'smooth' };
    return { top: 0 };
  },
  routes: [
    {
      path: '/',
      name: 'dashboard',
      component: Dashboard,
    },
    {
      path: '/campaigns',
      name: 'campaigns',
      component: () => import('../views/Campaigns.vue'),
    },
    {
      path: '/campaigns/:id',
      name: 'campaign-detail',
      component: () => import('../views/CampaignDetail.vue'),
    },
    {
      path: '/events',
      name: 'events',
      component: () => import('../views/Events.vue'),
    },
    {
      path: '/locations',
      name: 'locations',
      component: () => import('../views/Locations.vue'),
    },
    {
      path: '/locations/:id',
      name: 'location-detail',
      component: () => import('../views/LocationDetail.vue'),
    },
    {
      path: '/attendees',
      name: 'attendees',
      component: () => import('../views/Attendees.vue'),
    },
    {
      path: '/attendees/:id',
      name: 'attendee-detail',
      component: () => import('../views/AttendeeDetail.vue'),
    },
    {
      path: '/leads',
      name: 'leads',
      component: () => import('../views/LeadLists.vue'),
    },
    {
      path: '/leads/:id',
      name: 'lead-list-detail',
      component: () => import('../views/LeadListDetail.vue'),
    },
    {
      path: '/attendee-queue',
      name: 'attendee-queue',
      component: () => import('../views/AttendeeQueue.vue'),
    },
    {
      path: '/communication',
      name: 'communication',
      component: () => import('../views/Communication.vue'),
    },
    {
      path: '/configuration',
      name: 'configuration',
      component: () => import('../views/Configuration.vue'),
    },
    {
      path: '/reports',
      name: 'reports',
      component: () => import('../views/Reports.vue'),
    },
    {
      path: '/planner',
      name: 'planner',
      component: () => import('../views/EventPlanner.vue'),
      meta: { requiresManager: true, requiresFeature: 'planning_periods' },
    },
    {
      path: '/events/:id/roster',
      name: 'event-roster',
      component: () => import('../views/EventRoster.vue'),
    },
    // ── Billing routes (authenticated) ──
    {
      path: '/billing',
      name: 'billing',
      component: () => import('../views/Billing.vue'),
    },
    {
      path: '/upgrade',
      name: 'upgrade',
      component: () => import('../views/Upgrade.vue'),
    },
    {
      path: '/upgrade/:planId',
      name: 'upgrade-plan',
      component: () => import('../views/Upgrade.vue'),
    },
    // ── Onboarding (authenticated, no org required) ──
    {
      path: '/onboarding',
      name: 'onboarding',
      component: () => import('../views/Onboarding.vue'),
      meta: { requiresAuth: true, skipOrgCheck: true },
    },
    // ── Public routes ──
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/Login.vue'),
    },
    {
      path: '/logout',
      name: 'logout',
      component: () => import('../views/Logout.vue'),
    },
    {
      path: '/signup',
      name: 'signup',
      component: () => import('../views/Signup.vue'),
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('../views/ForgotPassword.vue'),
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('../views/ResetPassword.vue'),
    },
    {
      path: '/auth/callback',
      name: 'auth-callback',
      component: () => import('../views/AuthCallback.vue'),
    },
    {
      path: '/verify-email',
      name: 'verify-email',
      component: () => import('../views/VerifyEmail.vue'),
    },
    {
      path: '/docs',
      name: 'docs',
      component: () => import('../views/DocsPage.vue'),
    },
    {
      path: '/confirm/:id',
      name: 'confirm-attendance',
      component: () => import('../views/ConfirmAttendance.vue'),
    },
    {
      path: '/unsubscribe/:id',
      name: 'unsubscribe',
      component: () => import('../views/Unsubscribe.vue'),
    },
    {
      path: '/privacy',
      name: 'privacy-policy',
      component: () => import('../views/PrivacyPolicy.vue'),
    },
    {
      path: '/terms',
      name: 'terms-of-service',
      component: () => import('../views/TermsOfService.vue'),
    },
    {
      path: '/workspace-not-found',
      name: 'workspace-not-found',
      component: () => import('../views/WorkspaceNotFound.vue'),
    },
    // ── Dev-only routes ──
    ...(import.meta.env.DEV ? [
      {
        path: '/style-guide',
        name: 'style-guide',
        component: () => import('../views/StyleGuide.vue'),
      },
    ] : []),
  ],
});

// The marketing pages live on the static site; the SPA's old copies of them
// were indexed alongside it. Each moved path maps to its www equivalent.
const MARKETING_SITE = 'https://www.event-flow.co';
const MOVED_TO_MARKETING_SITE = {
  '/home': '/',
  '/features': '/features',
  '/pricing': '/pricing',
  '/for/financial-services': '/for/financial-services',
};

// Navigation guard to check authentication
router.beforeEach(async (to) => {
  const authStore = useAuthStore();
  const tenantStore = useTenantStore();

  // Wait for the tenant lookup before gating. vue-router kicks off the
  // initial navigation at app.use(router) — before main.js finishes awaiting
  // tenantStore.initialize() — so without this the not-found gate never
  // fired on the first page load. initialize() is memoized: instant after
  // the first resolution.
  await tenantStore.initialize();

  // If the URL implied a tenant subdomain but no org matched, lock the user
  // on the not-found page regardless of auth state. Skip the guard for the
  // not-found view itself to avoid an infinite redirect.
  if (tenantStore.status === 'not_found' && to.path !== '/workspace-not-found') {
    return '/workspace-not-found';
  }

  // Wait for auth to initialize if needed
  if (!authStore.initialized) {
    await authStore.initialize();
  }

  // Tenant gate rejected the signed-in user (mismatch OR no org membership).
  // The auth store has already signed them out and set the message; route
  // them to the workspace-not-found view which renders both cases.
  if (authStore.tenantAccessDenied && to.path !== '/workspace-not-found') {
    return '/workspace-not-found';
  }

  // Allow access to public pages without authentication
  const publicPaths = ['/login', '/logout', '/signup', '/forgot-password', '/reset-password', '/auth/callback', '/verify-email', '/docs', '/confirm/', '/unsubscribe/', '/privacy', '/terms', '/style-guide', '/workspace-not-found'];
  const isPublicPath = publicPaths.some(p => to.path === p || to.path.startsWith(p));

  // On tenant-gated subdomains the public marketing pages don't make sense —
  // unauthenticated visitors go straight to the tenant's login.
  if (tenantStore.isTenantGated && !authStore.isAuthenticated && (to.path === '/home' || to.path === '/')) {
    return '/login';
  }

  const movedTo = MOVED_TO_MARKETING_SITE[to.path];
  if (movedTo !== undefined) {
    const query = new URLSearchParams(to.query).toString();
    window.location.replace(MARKETING_SITE + movedTo + (query ? `?${query}` : '') + to.hash);
    return false;
  }

  if (!authStore.isAuthenticated && !isPublicPath) {
    return '/home';
  }

  // /verify-email requires a session to display the user's email and resend.
  if (!authStore.isAuthenticated && to.path === '/verify-email') {
    return '/login';
  }

  // Block unverified users from accessing any protected route.
  // They land on /verify-email until they click the confirmation link.
  const emailVerified = !!authStore.user?.email_confirmed_at;
  if (authStore.isAuthenticated && !emailVerified && !isPublicPath) {
    return '/verify-email';
  }

  // Redirect authenticated users away from login/signup to dashboard
  if (authStore.isAuthenticated && (to.path === '/login' || to.path === '/signup')) {
    return '/';
  }

  // If authenticated but no organization, redirect to onboarding
  // (unless already going to onboarding or a public path)
  if (authStore.isAuthenticated && !authStore.hasOrganization && !isPublicPath && to.path !== '/onboarding') {
    if (!to.meta.skipOrgCheck) {
      return '/onboarding';
    }
  }

  // Check for manager/admin required routes
  if (to.meta.requiresManager) {
    const userRole = authStore.userRole;
    if (!['manager', 'administrator'].includes(userRole)) {
      return '/';
    }
  }

  // Check for subscription feature requirements
  if (to.meta.requiresFeature) {
    const { isFeatureEnabled, getRequiredPlan, refreshUsage } = useSubscription();
    await refreshUsage();
    if (!isFeatureEnabled(to.meta.requiresFeature)) {
      const requiredPlan = getRequiredPlan(to.meta.requiresFeature);
      return { name: 'upgrade-plan', params: { planId: requiredPlan } };
    }
  }
});

// SPA page views — fires on every resolved navigation (including the initial
// one). analytics.js routes each hit to the marketing or app GA4 property
// based on the destination path.
router.afterEach((to) => {
  trackPageView(to.fullPath);
});

export default router;
